import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  Input,
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@gearbox-protocol/deploy-tools-ui";
import type { FC } from "react";
import { useMemo, useState } from "react";
import type { Address } from "viem";

export interface CreditManagerOption {
  address: Address;
  label: string;
}

export interface CreditManagerInputProps {
  value: Address;
  onChange: (value: Address) => void;
  options: CreditManagerOption[];
  loading?: boolean;
}

/**
 * Combobox that allows to pick a credit manager attached to the SDK
 * (showing its label below the address) or to type an address manually
 */
const CreditManagerInput: FC<CreditManagerInputProps> = props => {
  const { value, onChange, options, loading } = props;
  const [open, setOpen] = useState(false);

  const filtered = useMemo(() => {
    const v = value.toLowerCase();
    return options.filter(
      o =>
        o.address.toLowerCase().includes(v) ||
        o.label.toLowerCase().includes(v),
    );
  }, [options, value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Command shouldFilter={false}>
        <PopoverAnchor asChild>
          <Input
            value={value}
            onChange={e => onChange(e.target.value as Address)}
            onKeyDown={e => setOpen(e.key !== "Escape")}
            onMouseDown={() => setOpen(open => !open)}
            onFocus={() => setOpen(true)}
            placeholder="0x..."
          />
        </PopoverAnchor>
        {!open && <CommandList aria-hidden="true" className="hidden" />}
        <PopoverContent
          asChild
          portal={false}
          onOpenAutoFocus={e => e.preventDefault()}
          onInteractOutside={e => {
            if (e.target instanceof HTMLInputElement) {
              e.preventDefault();
            }
          }}
          className="w-[var(--radix-popover-trigger-width)] p-0"
          style={{ width: "var(--radix-popover-trigger-width)" }}
        >
          <CommandList>
            {filtered.length > 0 && (
              <CommandGroup>
                {filtered.map(o => (
                  <CommandItem
                    key={o.address}
                    value={o.address}
                    onMouseDown={e => e.preventDefault()}
                    onSelect={() => {
                      onChange(o.address);
                      setOpen(false);
                    }}
                    className="flex-col items-start gap-0"
                  >
                    <span>{o.address}</span>
                    <span className="text-muted-foreground text-sm">
                      {o.label}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            <CommandEmpty>
              {loading
                ? "Loading credit managers..."
                : "No matching credit managers, enter address manually"}
            </CommandEmpty>
          </CommandList>
        </PopoverContent>
      </Command>
    </Popover>
  );
};

export default CreditManagerInput;
